import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";

import { GoogleMapsLoaderService } from "../services/google-map-loader.service";

@Component({
  selector: "app-google-address",
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GoogleAddressComponent),
      multi: true,
    },
  ],
  templateUrl: "./google-address.component.html",
  styleUrl: "./google-address.component.scss",
})
export class GoogleAddressComponent
  implements ControlValueAccessor, AfterViewInit
{
  private readonly loader = inject(GoogleMapsLoaderService);

  @ViewChild("inputEl", { static: true })
  inputEl!: ElementRef<HTMLInputElement>;

  // UI inputs
  @Input() label = "Address";
  @Input() placeholder = "Enter address";
  @Input() required = false;

  // Validation styling flags (so parent decides when it’s invalid/touched)
  @Input() invalid = false;
  @Input() touched = false;

  // Places options
  @Input() types: string[] = ["address"];
  @Input() country?: string; // e.g. 'ca'
  @Input() fields: string[] = [
    "formatted_address",
    "address_components",
    "geometry",
    "name",
  ];

  // Output full place if parent wants to parse components
  @Output() placeSelected = new EventEmitter<google.maps.places.PlaceResult>();

  // CVA state
  value = "";
  disabled = false;

  private autocomplete?: google.maps.places.Autocomplete;
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  async ngAfterViewInit() {
    await this.loader.load();

    if (!google?.maps?.places?.Autocomplete) {
      // Loader must include Places library
      console.error(
        'Google Places not available. Ensure loader loads "places" library.',
      );
      return;
    }

    const opts: google.maps.places.AutocompleteOptions = {
      types: this.types as any,
      fields: this.fields as any,
    };

    if (this.country) {
      opts.componentRestrictions = { country: this.country };
    }

    this.autocomplete = new google.maps.places.Autocomplete(
      this.inputEl.nativeElement,
      opts,
    );

    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete?.getPlace();
      if (!place) return;

      const formatted =
        place.formatted_address ||
        place.name ||
        this.inputEl.nativeElement.value ||
        "";

      // update input + reactive form
      this.value = formatted;
      this.inputEl.nativeElement.value = formatted;

      this.onChange(formatted);
      this.onTouched();

      this.placeSelected.emit(place);
    });
  }

  // When user types manually (no selection)
  onInput(evt: Event) {
    const v = (evt.target as HTMLInputElement).value ?? "";
    this.value = v;
    this.onChange(v);
  }

  onBlur() {
    this.onTouched();
  }

  // ControlValueAccessor
  writeValue(v: string | null): void {
    this.value = v ?? "";
    if (this.inputEl) this.inputEl.nativeElement.value = this.value;
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.inputEl) this.inputEl.nativeElement.disabled = isDisabled;
  }
}
